import { GlAvatar, GlAvatarLink, GlLink, GlTooltipDirective, GlButton, GlDisclosureDropdown, GlToastMixin } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { isEmpty } from 'lodash-es';
import { produce } from 'immer';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { getIdFromGraphQLId, convertToGraphQLId } from '~/graphql_shared/utils';
import AwardsList from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/awards_list.vue?vue3';
import { __ } from '~/locale';
import { setUrlFragment } from '~/lib/utils/url_utility';
import ImportedBadge from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/imported_badge.vue?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import EmojiPicker from '/builds/gitlab/gitlab-ee/app/assets/javascripts/emoji/components/picker.vue?vue3';
import TimelineEntryItem from '~/vue_shared/components/notes/timeline_entry_item.vue';
import TimeAgoTooltip from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/time_ago_tooltip.vue?vue3';
import { TYPENAME_USER } from '~/graphql_shared/constants';
import updateNoteMutation from '../graphql/update_note.mutation.graphql';
import designNoteAwardEmojiToggleMutation from '../graphql/design_note_award_emoji_toggle.mutation.graphql';
import getDesignQuery from '../graphql/design_details.query.graphql';
import { findNoteId } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/design_management/utils.js?vue3';
import { hasErrors } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/design_management/cache_updates.js?vue3';
import { AWARD_EMOJI_TO_NOTE_ERROR } from '../constants';
import DesignReplyForm from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/design_management/design_notes/design_reply_form.vue?vue3';
export default {
    name: 'DesignNote',
    i18n: {
        editCommentLabel: __('Edit comment'),
        moreActionsLabel: __('More actions'),
        deleteCommentText: __('Delete comment'),
        copyCommentLink: __('Copy link')
    },
    components: {
        AwardsList,
        GlAvatar,
        GlButton,
        GlAvatarLink,
        GlLink,
        GlDisclosureDropdown,
        ImportedBadge,
        TimeAgoTooltip,
        TimelineEntryItem,
        DesignReplyForm,
        EmojiPicker
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        GlToastMixin
    ],
    props: {
        note: {
            type: Object,
            required: true
        },
        noteableId: {
            type: String,
            required: true
        },
        designVariables: {
            type: Object,
            required: true
        },
        isDiscussion: {
            type: Boolean,
            required: false,
            default: false
        },
        markdownPreviewPath: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: [
        'delete-note',
        'error'
    ],
    data () {
        return {
            isEditing: false
        };
    },
    computed: {
        mutationVariables () {
            return {
                id: this.note.id
            };
        },
        author () {
            return this.note.author || {};
        },
        hasAuthor () {
            return !isEmpty(this.author);
        },
        authorId () {
            return getIdFromGraphQLId(this.author.id);
        },
        noteAnchorId () {
            return findNoteId(this.note.id);
        },
        isImported () {
            return this.note.imported;
        },
        currentUserId () {
            return window.gon?.current_user_id;
        },
        currentUserFullName () {
            return window.gon?.current_user_fullname;
        },
        canAwardEmoji () {
            return this.note.userPermissions.awardEmoji;
        },
        adminPermissions () {
            return this.note.userPermissions.adminNote;
        },
        hasPermissions () {
            return this.adminPermissions;
        },
        awards () {
            return this.note.awardEmoji.nodes.map((award)=>{
                return {
                    ...award,
                    user: {
                        ...award.user,
                        id: getIdFromGraphQLId(award.user.id)
                    }
                };
            });
        },
        dropdownItems () {
            return [
                {
                    text: this.$options.i18n.editCommentLabel,
                    action: ()=>{
                        this.isEditing = true;
                    },
                    extraAttrs: {
                        class: '@sm/panel:!gl-hidden'
                    }
                },
                {
                    text: this.$options.i18n.copyCommentLink,
                    action: ()=>{
                        this.$toast.show(__('Link copied to clipboard.'));
                    },
                    extraAttrs: {
                        'data-clipboard-text': setUrlFragment(window.location.href, `note_${this.noteAnchorId}`)
                    }
                },
                {
                    text: this.$options.i18n.deleteCommentText,
                    action: ()=>{
                        this.$emit('delete-note', this.note);
                    },
                    variant: 'danger',
                    extraAttrs: {
                        class: [
                            {
                                '!gl-hidden': !this.hasPermissions
                            }
                        ]
                    }
                }
            ];
        }
    },
    methods: {
        hideForm () {
            this.isEditing = false;
        },
        onDone ({ data }) {
            this.hideForm();
            if (hasErrors(data.updateNote)) {
                this.$emit('error', data.errors[0]);
            }
        },
        isEmojiPresentForCurrentUser (name) {
            return this.awards.findIndex((emoji)=>emoji.name === name && emoji.user.id === this.currentUserId) > -1;
        },
        /**
     * Prepare emoji reaction nodes based on emoji name
     * and whether the user has toggled the emoji off or on
     */ getAwardEmojiNodes (name, toggledOn) {
            // If the emoji toggled on, add the emoji
            if (toggledOn) {
                // If emoji is already present in award list, no action is needed
                if (this.isEmojiPresentForCurrentUser(name)) {
                    return this.note.awardEmoji.nodes;
                }
                // else return the list with new emoji added
                const newEmoji = {
                    name,
                    __typename: 'AwardEmoji',
                    user: {
                        id: convertToGraphQLId(TYPENAME_USER, this.currentUserId),
                        name: this.currentUserFullName,
                        __typename: 'UserCore'
                    }
                };
                return [
                    ...this.note.awardEmoji.nodes,
                    newEmoji
                ];
            }
            // else just filter the emoji
            return this.note.awardEmoji.nodes.filter((emoji)=>!(emoji.name === name && getIdFromGraphQLId(emoji.user.id) === this.currentUserId));
        },
        async handleAwardEmoji (name) {
            try {
                await this.$apollo.mutate({
                    mutation: designNoteAwardEmojiToggleMutation,
                    variables: {
                        name,
                        awardableId: this.note.id
                    },
                    optimisticResponse: {
                        awardEmojiToggle: {
                            errors: [],
                            toggledOn: !this.isEmojiPresentForCurrentUser(name)
                        }
                    },
                    update: (cache, { data: { awardEmojiToggle: { toggledOn } } })=>{
                        const query = {
                            query: getDesignQuery,
                            variables: this.designVariables
                        };
                        const sourceData = cache.readQuery(query);
                        const newData = produce(sourceData, (draftState)=>{
                            const { awardEmoji } = draftState.designManagement.designAtVersion.design.discussions.nodes.find((d)=>d.id === this.note.discussion.id).notes.nodes.find((n)=>n.id === this.note.id);
                            awardEmoji.nodes = this.getAwardEmojiNodes(name, toggledOn);
                        });
                        cache.writeQuery({
                            ...query,
                            data: newData
                        });
                    }
                });
            } catch (error) {
                Sentry.captureException(error);
                this.$emit('error', AWARD_EMOJI_TO_NOTE_ERROR);
            }
        }
    },
    updateNoteMutation
};
